import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import HeaderPage from "../components/HeaderPage";
import BoletinFinalList from "../components/Boletin/BoletinFinalList";
import ProgressModal from "../components/ProgressModal";
import Notification from "../components/Notification";
import DropDown from "../components/DropDown";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { useBoletin } from "../context/BoletinContext";
import { useUsers } from "../context/UsersContext";

const BoletinFinalPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { salones, fetchSalones, salonGrado, fetchSalonForDirector } = useUsers();
  const {
    loading,
    estudiantes,
    progreso,
    fetchEstudiantesSalon,
    generarBoletinesSalon,
    generarBoletinEstudiante
  } = useBoletin();

  const [selectedSalon, setSelectedSalon] = useState("");
  const [notification, setNotification] = useState(null);
  const [showProgress, setShowProgress] = useState(false);

  const closeNotification = () => setNotification(null);

  // **AGREGAR VALIDACIÓN DE PREESCOLAR**
  const grado = salonGrado?.salon_grado?.toLowerCase();
  const esPreescolar = ["parvulos", "prejardin", "jardin", "transicion"].includes(grado);

  // Para admin: obtener el grado del salón seleccionado
  const salonSeleccionado = salones.find((s) => s.id === parseInt(selectedSalon));
  const gradoAdmin = salonSeleccionado?.grado
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  const esPreescolarAdmin = ["parvulos", "prejardin", "jardin", "transicion"].includes(gradoAdmin);

  

  useEffect(() => {
    if (user?.rol_id === 1) {
      fetchSalones(); // Admin ve todos los salones
    } else if (user?.rol_id === 2) {
      fetchSalonForDirector(); // Director ve solo su salón
    }
  }, [user]);

  // Para directores, seleccionar automáticamente su salón
  useEffect(() => {
    if (user?.rol_id === 2 && salonGrado?.salon_id) {
      setSelectedSalon(salonGrado.salon_id.toString());
      fetchEstudiantesSalon(salonGrado.salon_id);
    }
  }, [salonGrado, fetchEstudiantesSalon, user]);

  const handleSalonChange = async (e) => {
    const salonId = e.target.value;
    setSelectedSalon(salonId);
    
    if (salonId) {
      // Validar si el salón seleccionado es de preescolar
      const salon = salones.find(s => s.id === parseInt(salonId));
      const gradoSalon = salon?.grado?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      
      if (["parvulos", "prejardin", "jardin", "transicion"].includes(gradoSalon)) {
        setNotification({
          type: "warning",
          message: "Los boletines finales no están disponibles para grados de preescolar."
        });
        return;
      }

      try {
        await fetchEstudiantesSalon(parseInt(salonId));
      } catch (error) {
        setNotification({
          type: "error",
          message: "Error al cargar estudiantes del salón."
        });
      }
    }
  };

  const salonActual = user?.rol_id === 2 && salonGrado 
    ? { 
        id: salonGrado.salon_id, 
        nombre: salonGrado.salon_nombre, 
        grado: salonGrado.salon_grado 
      } 
    : salones.find(s => s.id === parseInt(selectedSalon));

  const handleGenerarTodos = async () => {
    if (!selectedSalon) {
      setNotification({
        type: "error",
        message: "Debe seleccionar un salón primero."
      });
      return;
    }

    if (estudiantes.length === 0) {
      setNotification({
        type: "error",
        message: "No hay estudiantes en este salón."
      });
      return;
    }

    setShowProgress(true);
    try {
      const resultados = await generarBoletinesSalon(parseInt(selectedSalon));
      
      const exitosos = resultados.filter(r => r.exito).length;
      const fallidos = resultados.filter(r => !r.exito).length;
      
      if (fallidos === 0) {
        setNotification({
          type: "success",
          message: `¡Todos los boletines generados correctamente! (${exitosos} boletines)`
        });
      } else {
        setNotification({
          type: "warning",
          message: `Generación completada: ${exitosos} exitosos, ${fallidos} fallidos.`
        });
      }
    } catch (error) {
      setNotification({
        type: "error",
        message: "Error al generar los boletines finales."
      });
    } finally {
      setShowProgress(false);
    }
  };

  const handleGenerarIndividual = async (studentId) => {
    try {
      await generarBoletinEstudiante(studentId);
      setNotification({
        type: "success",
        message: "Boletín final generado correctamente."
      });
      
      // Refrescar la lista
      if (selectedSalon) {
        await fetchEstudiantesSalon(parseInt(selectedSalon));
      }
    } catch (error) {
      setNotification({
        type: "error",
        message: "Error al generar el boletín final."
      });
    }
  };

  // **AGREGAR VALIDACIÓN PARA DIRECTORES DE PREESCOLAR**
  if (user?.rol_id === 2 && salonGrado && esPreescolar) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 p-6 overflow-y-auto">
          <HeaderPage title="5º Período - Boletines Finales" />
          <div className="bg-white p-8 rounded-2xl shadow-md mt-10 flex flex-col items-center text-center">
            <p className="text-xl font-semibold max-w-md mb-4">
              Esta funcionalidad solo está disponible para grados de primaria.
            </p>
            <p className="text-gray-600 mb-6">
              Los boletines finales no aplican para preescolar (Párvulos, Prejardín, Jardín, Transición).
            </p>
            <img
              src="/logo.png"
              alt="Logo Colegio"
              className="w-64 mb-4 drop-shadow-xl"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/boletin')}
            className="flex items-center gap-2 px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-blue-800 transition"
          >
            <FaArrowLeft />
            Volver
          </button>
        </div>
        <HeaderPage title="5º Período - Boletines Finales" />
        
        {loading && !showProgress ? (
          <p className="text-center text-xl font-semibold">Cargando...</p>
        ) : (
          <>
            {/* Selector de salón (solo para admin) */}
            {user?.rol_id === 1 && (
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h3 className="text-lg font-semibold mb-4">Seleccionar Salón</h3>
                <DropDown
                  value={selectedSalon}
                  onChange={handleSalonChange}
                  placeholder="Seleccione un salón"
                  options={salones.map((salon) => ({
                    value: salon.id,
                    label: `${salon.nombre} - ${salon.grado}`
                  }))}
                />

                {/* **MOSTRAR MENSAJE INFORMATIVO** */}
                <p className="text-sm text-gray-500 mt-2">
                  * Solo se muestran salones de primaria. Los boletines finales no aplican para preescolar.
                </p>
              </div>
            )}

            {/* **MOSTRAR MENSAJE SI ADMIN NO TIENE SALONES DE PRIMARIA** */}
            {user?.rol_id === 1 && salones.length > 0 && 
             salones.filter(salon => {
               const gradoSalon = salon.grado?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
               return !["parvulos", "prejardin", "jardin", "transicion"].includes(gradoSalon);
             }).length === 0 && (
              <div className="bg-white p-8 rounded-2xl shadow-md mt-10 flex flex-col items-center text-center">
                <p className="text-xl font-semibold max-w-md mb-4">
                  No hay salones de primaria disponibles.
                </p>
                <p className="text-gray-600 mb-6">
                  Los boletines finales solo están disponibles para grados de primaria.
                </p>
                <img
                  src="/logo.png"
                  alt="Logo Colegio"
                  className="w-64 mb-4 drop-shadow-xl"
                />
              </div>
            )}

            {/* Información del salón seleccionado */}
            {salonActual && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md mb-6 border-l-4 border-blue-500">
                <h3 className="text-xl font-semibold text-blue-700 mb-2">
                  {salonActual.nombre} - {salonActual.grado}
                </h3>
                <p className="text-gray-600">
                  Total de estudiantes: <span className="font-bold">{estudiantes.length}</span>
                </p>
                <p className="text-gray-600">
                  Boletines generados: <span className="font-bold">{estudiantes.filter(e => e.boletin_final_url).length}</span>
                </p>
              </div>
            )}

            {/* Lista de estudiantes y boletines */}
            {selectedSalon && (
              <BoletinFinalList
                estudiantes={estudiantes}
                salonInfo={salonActual}
                onGenerarTodos={handleGenerarTodos}
                onGenerarIndividual={handleGenerarIndividual}
                loading={loading}
              />
            )}

            {!selectedSalon && user?.rol_id === 1 && (
              <div className="bg-white p-8 rounded-2xl shadow-md mt-10 flex flex-col items-center text-center">
                <p className="text-xl font-semibold max-w-md">
                  Seleccione un salón para ver los estudiantes y generar los boletines finales.
                </p>
                <img
                  src="/logo.png"
                  alt="Logo Colegio"
                  className="w-64 mb-4 drop-shadow-xl"
                />
              </div>
            )}
          </>
        )}

        {/* Modal de progreso */}
        {showProgress && (
          <ProgressModal
            isOpen={showProgress}
            progreso={progreso}
            onClose={() => setShowProgress(false)}
          />
        )}

        {/* Notificaciones */}
        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={closeNotification}
          />
        )}
      </div>
    </div>
  );
};

export default BoletinFinalPage;